import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SITE } from "@/constants/site";
import { RevealLine } from "@/components/animations/Reveal";

export default function Hero() {
    const { t } = useLanguage();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const yWallLogo = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
    const scaleWallLogo = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

    return (
        <section
            id="home"
            ref={ref}
            className="relative min-h-[100svh] w-full overflow-hidden bg-ink"
            data-testid="hero"
        >
            {/* Background image — the model at the styling chair */}
            <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-[1]">
                <img
                    src={SITE.images.hero}
                    alt="Frizerka salon interior"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 noise" />
            </motion.div>

            {/* Top gold hairline */}
            <div className="absolute top-20 left-0 right-0 mx-auto max-w-[1200px] px-8 z-[3]">
                <div className="gold-hairline w-full" />
            </div>

            <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 pt-28 sm:pt-40 pb-16 sm:pb-24 min-h-[100svh] flex flex-col">
                {/* Centered heading & CTAs */}
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-5 sm:gap-7 -mt-6">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35, duration: 0.8 }}
                        className="flex items-center gap-4 text-white"
                    >
                        <span className="h-[1px] w-10 bg-white/70" />
                        <span className="eyebrow !text-white/90">{t.hero.eyebrow}</span>
                        <span className="h-[1px] w-10 bg-white/70" />
                    </motion.div>

                    <h1
                        className="font-display text-white leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-[7.5rem] mt-2"
                        data-testid="hero-title"
                        style={{ textShadow: "0 4px 30px rgba(0,0,0,0.45)" }}
                    >
                        <RevealLine delay={0.35}>{t.hero.title_1}</RevealLine>
                        <br />
                        <span className="serif-italic text-gold-soft text-4xl sm:text-6xl lg:text-7xl">
                            <RevealLine delay={0.55}>{t.hero.title_2}</RevealLine>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.9 }}
                        className="max-w-xl text-white/95 text-base sm:text-lg font-light tracking-wide"
                        data-testid="hero-subtitle"
                        style={{ textShadow: "0 2px 14px rgba(0,0,0,0.5)" }}
                    >
                        {t.hero.subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.9 }}
                        className="w-full sm:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-2 px-4 sm:px-0"
                    >
                        <a
                            href={SITE.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-gold w-full sm:w-auto text-center"
                            data-testid="hero-cta-book"
                        >
                            {t.hero.cta}
                        </a>
                        <button
                            onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                            className="btn-outline w-full sm:w-auto !text-white !border-white/60 hover:!bg-white hover:!text-ink"
                            data-testid="hero-cta-services"
                        >
                            {t.hero.secondary}
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.35, duration: 0.9 }}
                        className="mt-4 sm:mt-6 flex items-center gap-2.5 text-white/90 max-w-full px-2"
                        data-testid="hero-keune"
                    >
                        <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full border border-gold/60 shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                        </span>
                        <span className="text-[0.6rem] sm:text-[0.7rem] tracking-[0.2em] sm:tracking-[0.35em] uppercase font-medium text-center">
                            {t.hero.keune}
                        </span>
                    </motion.div>
                </div>

                {/* Scroll cue */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5, duration: 1 }}
                    className="flex flex-col items-center gap-3 text-white/80"
                >
                    <span className="text-[0.65rem] tracking-[0.4em] uppercase">Scroll</span>
                    <motion.div
                        animate={{ y: [0, 8, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <ArrowDown className="w-4 h-4" />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
